import { IDLE_ACTIVITY_STORAGE_KEYS } from '@supply-mover-2-0/core';
import { IdleTimeoutGuard, useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOutPortal } from '../lib/portalSignOut';
import { useCartStore } from '../stores/cartStore';
import { useLoginModalStore } from '../stores/loginModalStore';
import { usePortalAuthStore } from '../stores/portalAuthStore';

/**
 * PD-16414 — the storefront's idle sign-out. Mounted once inside TenantGate.
 *
 * The limit is the tenant's `security.idle_timeout.storefrontMinutes`, which
 * arrives with /auth/me; the guard waits until it does and does nothing when
 * it is off. On a sign-out the shopper lands on the home page with the
 * sign-in dialog open, saying why, which is the same place the Sign Out menu
 * item leaves them.
 *
 * `local`: this tab reached the limit, so it revokes the session. `remote`:
 * another tab's sign-out was heard first; this one only clears itself and
 * shows the same message. (Two tabs reaching the limit in the same instant
 * both revoke the same tokens, which is harmless.)
 */
export function PortalIdleTimeout(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const isAuthenticated = usePortalAuthStore((s) => s.isAuthenticated);
	const limitMinutes = usePortalAuthStore((s) => s.idleTimeoutMinutes);

	return (
		<IdleTimeoutGuard
			isAuthenticated={isAuthenticated}
			limitMinutes={limitMinutes}
			storageKey={IDLE_ACTIVITY_STORAGE_KEYS.storefront}
			onTimeout={(source) => {
				const message =
					limitMinutes != null
						? t(
								'You were signed out after {{minutes}} minutes of inactivity.',
								{ minutes: limitMinutes },
							)
						: t('You were signed out because of inactivity.');
				void signOutPortal({ revoke: source === 'local' });
				useCartStore.getState().setCart(null);
				void navigate('/');
				useLoginModalStore.getState().open(message);
			}}
		/>
	);
}
